
import { useState } from "react";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import AuthForm from "./AuthForm";
import GoogleAuthButton from "./GoogleAuthButton";

interface AuthModalContentProps {
  onClose: () => void;
  defaultTab?: 'signin' | 'signup';
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export default function AuthModalContent({ onClose, defaultTab = 'signin', onSuccess, onError }: AuthModalContentProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { t } = useLanguage();

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          {activeTab === 'signin' ? t('auth.signIn') : t('auth.signUp')}
        </DialogTitle>
      </DialogHeader>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'signin' | 'signup')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
          <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
        </TabsList>

        <TabsContent value="signin" className="space-y-4 mt-6">
          <GoogleAuthButton onSuccess={onSuccess} onError={onError} />
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('auth.continueWith')}
              </span>
            </div>
          </div>
          <AuthForm type="signin" onClose={onClose} onSuccess={onSuccess} onError={onError} />
          
          <div className="text-center text-sm">
            <span className="text-muted-foreground">{t('auth.noAccount')} </span>
            <button
              onClick={() => setActiveTab('signup')}
              className="text-primary underline hover:text-primary/80"
            >
              {t('auth.signUp')}
            </button>
          </div>
        </TabsContent>

        <TabsContent value="signup" className="space-y-4 mt-6">
          <GoogleAuthButton onSuccess={onSuccess} onError={onError} />
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('auth.continueWith')}
              </span>
            </div>
          </div>
          <AuthForm type="signup" onClose={onClose} onSuccess={onSuccess} onError={onError} />
          
          <div className="text-center text-sm">
            <span className="text-muted-foreground">{t('auth.haveAccount')} </span>
            <button
              onClick={() => setActiveTab('signin')}
              className="text-primary underline hover:text-primary/80"
            >
              {t('auth.signIn')}
            </button>
          </div>
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
}
